import { NgTemplateOutlet, NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  untracked,
  TemplateRef,
} from '@angular/core';
import { NgbCarouselModule } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from '../../../store/store.service';
import { FADE, GROW } from '../../animations/animations';
import { SafeHtml } from '../../pipes/SafeHtml.pipe';
import { CommonService } from '../../services/common.service';
import { ModalService } from '../../services/modal.service';
import { SidePanelService } from '../../services/side-panel.service';
import { RoomBookingData, RoomRateAvailabilityService } from '../../services/room-rate-availability.service';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { AmenitiesListComponent, Amenity, AmenityType } from '../amenities-list/amenities-list.componet';
import { ButtonComponent } from '../button/button.component';
import { CardComponent } from '../card/card.component';
import { CarouselComponent, CarouselConfig } from '../carousel/carousel.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { RoomRateCardComponent } from '../room-rate-card/room-rate-card.component';
import { SkeletonLoaderItemComponent } from '../skeleton-loader-item/skeleton-loader-item';
import { TagComponent } from '../tag/tag.component';
import { Room360TourComponent } from '../room-360-tour/room-360-tour.component';
import { NavTabsComponent } from '../nav-tabs/nav-tabs.component';
import { PriceBreakdownComponent } from '../price-breakdown/price-breakdown.component';
import { SidePanelComponent } from '../side-panel/side-panel.component';

@Component({
  standalone: true,
  imports: [
    CardComponent,
    CarouselComponent,
    NavTabsComponent,
    SafeHtml,
    TagComponent,
    AmenitiesListComponent,
    ButtonComponent,
    DropdownComponent,
    NgTemplateOutlet,
    RoomRateCardComponent,
    SidePanelComponent,
    NgbCarouselModule,
    PriceBreakdownComponent,
    SkeletonLoaderItemComponent,
    Room360TourComponent,
    NgClass,
  ],
  selector: 'app-room-card',
  templateUrl: 'room-card.component.html',
  styleUrl: 'room-card.component.scss',
  host: {
    '[@grow]': 'true',
    '(click)': 'onCardClick($event)',
  },
  animations: [GROW, FADE],
})
export class RoomCardComponent {
  roomDetailsContentTemplate = viewChild<TemplateRef<any>>('roomDetailsContent');
  roomRatesListContentTemplate = viewChild<TemplateRef<any>>('roomRatesListContent');

  // Inputs
  public readonly data = input.required<any>();
  public readonly index = input.required<number>();

  // Optional Inputs
  public readonly isLoading = input<boolean>(false);
  public readonly items = input<any[]>([]);
  public readonly isExpanded = input<boolean>(false);
  public readonly isChild = input<boolean>(false);
  public readonly isItemsLoading = input<boolean>(false);
  public readonly isDisabled = input<boolean>(false);
  public readonly noPadding = input<boolean>(false);
  public readonly noMarginBottom = input<boolean>(false);
  public readonly noBorder = input<boolean>(false);
  public readonly isAddToCartInProgress = input<boolean>(false);

  // Outputs
  public readonly onStateChanged = output<{ index: number; state: 'EXPANDED' | 'COLLAPSED' } | null>();
  public readonly onItemSelected = output<{
    room: any;
    bedtype: { isDefault: boolean; code: string };
  }>();
  public readonly onRoomRateSelected = output<{
    room: any;
    rate: any;
    bedtype: { isDefault: boolean; code: string };
  }>();

  // Signals
  public readonly roomDetailsModalVisible = signal<boolean>(false);
  public readonly roomDetailsClickedWhileCollapsed = signal<boolean>(false);
  public readonly selectedBedTypeCode = linkedSignal<string>(() => this.data()?.defaultBedType);
  public readonly roomDetailsNavTabs = linkedSignal(() => {
    const localeObj = this._storeSvc.localeObj();
    return [
      { key: 'ROOM_DETAILS', value: localeObj?.tf_2_RoomList_roomDetailsModal_tabRoomDetails },
      { key: 'PRICE_BREAKDOWN', value: localeObj?.tf_2_RoomList_priceBreakdownTitle },
    ];
  });
  public readonly roomDetailsActiveTab = signal<'ROOM_DETAILS' | 'PRICE_BREAKDOWN'>('ROOM_DETAILS');

  // Computed Signals
  public readonly isMobileScreen = computed(() => {
    return (this._storeSvc.res()?.mobile || this._storeSvc.res()?.tablet) ?? false;
  });
  public readonly roomImages = computed<{ src: string; alt: string }[]>(() => {
    if (this.isLoading()) {
      return [];
    }

    const data = this.data();
    const imageUrls = Array.isArray(data?.imageUrls)
      ? data?.imageUrls?.filter((imageUrl: any) => !!imageUrl?.largeImageUrl || !!imageUrl?.thumbnailImageUrl)
      : [];
    const images: { src: string; alt: any }[] = [];

    if (imageUrls.length > 0) {
      imageUrls.forEach((imageUrl: any, index: number) => {
        images.push({
          src: imageUrl?.largeImageUrl || imageUrl?.thumbnailImageUrl,
          alt: imageUrl?.imageAltText || `Room Image ${index + 1}`,
        });
      });
    }

    return images;
  });
  public readonly roomBedTypes = computed(() => {
    if (this.isLoading()) {
      return [];
    }

    const data = this.data();

    if (data?.bedTypes?.length > 1) {
      const bedTypes = Array.isArray(data?.bedTypes) ? data?.bedTypes : [];
      return bedTypes.map((bedType: any, index: number) => ({
        id: bedType.bedTypeCode,
        index: index,
        value: bedType.bedTypeName,
        iconSrc: bedType.bedTypeImageURL,
        ...bedType,
      }));
    }

    return [];
  });
  public readonly selectedBedType = computed(() => {
    const selectedBedTypeCode = this.selectedBedTypeCode();
    const bedTypes = this.roomBedTypes();
    return bedTypes.find((bedType: any) => bedType.bedTypeCode === selectedBedTypeCode);
  });
  public readonly carouselConfig = computed(
    (): CarouselConfig => ({
      showNavigationArrowsOnHover: !this.isMobileScreen(),
      loop: true,
      autoPlay: true,
      interval: 3000,
    }),
  );
  public readonly roomsRemainingMsg = computed<string>(() => {
    const noOfRooms = 2;
    const localeObj = this._storeSvc.localeObj();
    const isUpperCaseLabel = this._storeSvc.appStore.isUpperCaseLabel();
    return this._commonSvc.getUpperCaseString(
      isUpperCaseLabel,
      this._commonSvc.fillMessage(localeObj?.tf_2_RoomList_roomsRemaining, [
        noOfRooms,
        noOfRooms > 1 ? localeObj?.tf_2_RoomList_rooms_label : localeObj?.tf_2_RoomList_room_label,
      ]),
    );
  });
  public readonly roomAmenities = computed<Amenity[]>(() => {
    const data = this.data();
    return Array.isArray(data?.amenities) && data?.amenities?.length > 0 ? data?.amenities : [];
  });
  public readonly otherAmenities = computed<Amenity[]>(() => {
    const commonRoomAmenities = this._storeSvc.propInfo()?.commonRoomAmenities;
    return Array.isArray(commonRoomAmenities) && commonRoomAmenities?.length > 0 ? commonRoomAmenities : [];
  });
  public readonly hideRoomDetails = computed<boolean>(() => this._storeSvc.propInfo()?.hideRoomDetails ?? false);
  public readonly isUDProperty = computed(() => {
    const propertyType = this._storeSvc.propInfo()?.propertyType;
    return propertyType === 'UD';
  });
  public readonly isRelativePricing = computed(() => {
    const defaultPricing = this._storeSvc.propInfo()?.defaultPricing;
    return !!defaultPricing && defaultPricing === 'RELATIVE' ? true : false;
  });
  public readonly showRoomDescription = computed(() => {
    const showRoomDescription = this._storeSvc.propInfo()?.showRoomDescriptionOnListing ?? false;
    const description = this.data()?.shortDescription ?? '';
    return showRoomDescription && description;
  });
  public readonly showAmenities = computed(() => {
    const showAmenities = this._storeSvc.propInfo()?.displayAmenities ?? false;
    return showAmenities;
  });
  public readonly showRoomsRemainingMessage = computed(() => {
    const propertyInfo = this._storeSvc.propInfo();
    const room = this.data();
    const displayAvailableRooms = room?.bedTypes?.[0]?.displayAvailableRooms ?? false;
    const enableRoomsRemainingMessage = propertyInfo?.enableRoomsRemainingMessage ?? false;
    return displayAvailableRooms && enableRoomsRemainingMessage;
  });
  public readonly roomsRemainingMessage = computed<string>(() => {
    if (this.isLoading()) {
      return '';
    }

    const room = this.data();
    const noOfRoomsRemaining = room?.bedTypes?.[0]?.availableRooms ?? 0;
    const localeObj = this._storeSvc.localeObj();

    return this._commonSvc.fillMessage(localeObj?.tf_2_RoomList_pricePanel_roomsAvailabilityMsg, [
      noOfRoomsRemaining,
      noOfRoomsRemaining > 1 ? localeObj?.tf_4_Checkout_addons_roomsText : localeObj?.tf_4_Checkout_addons_roomLc,
    ]);
  });
  public readonly showRoomsBookedMessage = computed(
    () => () => this._storeSvc.propInfo()?.displayXRoomsBookedInLast24Hrs ?? false,
  );
  public readonly roomsBookedMessage = computed(() => {
    if (this.isLoading()) {
      return '';
    }

    const room = this.data();
    const localeObj = this._storeSvc.localeObj();
    const roomsBookedUpdates = this._roomRateAvailabilitySvc.getRoomBookedUpdatesForRoomCode(
      room.code,
    ) as RoomBookingData;

    const msg = this._commonSvc.fillMessage(localeObj.tf_2_RoomList_rooms_roomBookedPeriod, [
      roomsBookedUpdates?.totalBooked ?? 0,
      (roomsBookedUpdates?.totalBooked ?? 0 > 1)
        ? localeObj.tf_4_Checkout_addons_roomsText
        : localeObj.tf_4_Checkout_addons_roomLc,
      localeObj.tf_Generic_twentyFourHours || roomsBookedUpdates?.timeSpan || '',
    ]);
    return roomsBookedUpdates?.totalBooked > 0 ? msg : '';
  });
  public readonly showDPRMessage = computed(() => this._storeSvc.propInfo()?.displayDprTagline ?? false);
  public readonly pricing = computed(() => {
    if (this.isLoading()) {
      return null;
    }

    const room = this.data();
    const roomIndx = this.index();
    const localeObj = this._storeSvc.localeObj();
    const currency = this._storeSvc.userStore.currency();
    const showPriceInclusiveOfFees = this._shoppingCartSvc.showTaxesInDisplayPrice();

    let avgPrice = this._shoppingCartSvc.getAvgRoomPrice(room, currency.code, showPriceInclusiveOfFees);
    let discountedPrice = this._shoppingCartSvc.getDiscountedRoomPrice(room, currency.code, showPriceInclusiveOfFees);

    const hasLoyaltyRate = room.bedTypes.some((bedType: any) => bedType.hasLoyaltyOffers);
    const hasComplimentaryRate = room.bedTypes.some(
      (bedType: any) => bedType.isFullyComplimentary && bedType.hasComplimentaryNight,
    );
    const hasPartialCompRate = room.bedTypes.some(
      (bedType: any) => !bedType.isFullyComplimentary && bedType.hasComplimentaryNight,
    );
    const upsellPrice = this.getUpsellPrice(room, currency.code, showPriceInclusiveOfFees);

    let zeroRelativePriceText = '';
    let defaultPricingText = '';

    // this.roomupdates = this.roomListingService.getRoomBookedUpdatesForRoomCode(room.code);

    if (!this.isUDProperty()) {
      // Start - Code to show the Relative pricing format
      if (this.isRelativePricing()) {
        if (roomIndx === 0) {
          defaultPricingText = '';
          zeroRelativePriceText = '';
        } else {
          defaultPricingText = localeObj?.tf_2_RoomList_rooms_label_more || 'more'; // more
          zeroRelativePriceText = '';
        }
      } else {
        defaultPricingText = '';
        zeroRelativePriceText = '';
      }

      let baseRoomAvgPrice = avgPrice;
      let baseRoomDiscountedPrice = discountedPrice;

      if (roomIndx === 0) {
        baseRoomAvgPrice = avgPrice;
        baseRoomDiscountedPrice = discountedPrice;
      } else if (this.isRelativePricing()) {
        discountedPrice = Math.abs(discountedPrice - baseRoomDiscountedPrice);
        avgPrice = Math.abs(avgPrice - baseRoomAvgPrice);

        let isZeroPriceFlag = false;
        if (discountedPrice) {
          isZeroPriceFlag = discountedPrice === 0 && avgPrice === 0 ? true : false;
        } else {
          isZeroPriceFlag = avgPrice === 0 ? true : false;
        }

        if (isZeroPriceFlag) {
          const finalPrice = discountedPrice || avgPrice;
          const zeroRelativePriceLabel =
            localeObj?.tf_2_RoomList_rooms_label_zeroRelativePrice || 'Reserve for <b>{{$1}}{{$2}}</b> more';
          zeroRelativePriceText = this._commonSvc.fillMessage(zeroRelativePriceLabel, [currency?.symbol, finalPrice]);
        } else {
          zeroRelativePriceText = '';
        }
      }
    }

    // Tax Display
    const taxDisplay = this._shoppingCartSvc.getTaxDisplay(room, currency.code, currency.symbol, localeObj);

    return {
      avgPrice,
      discountedPrice,
      zeroRelativePriceText,
      defaultPricingText,
      hasLoyaltyRate,
      hasComplimentaryRate,
      hasPartialCompRate,
      upsellPrice,
      taxDisplay,
    };
    // End - Code to show the Relative pricing format
  });
  public readonly isRoomDetailsLoading = computed(() => {
    const roomCode = this.data()?.code;
    const roomDetailsData = this._roomRateAvailabilitySvc.roomDetailsData();
    return roomCode === roomDetailsData?.roomCode && roomDetailsData?.isLoading;
  });
  public readonly amenityType = computed((): AmenityType => this._storeSvc.propInfo().amenityIconText);
  public readonly roomDetails = computed(() => {
    const isRoomLoading = this.isLoading();
    const data = this._roomRateAvailabilitySvc.roomDetailsData();
    const localeObj = this._storeSvc.localeObj();
    const isLoading = isRoomLoading || data?.isLoading;

    if (isLoading) {
      return null;
    }

    const roomDetails = data?.roomDetails;
    const maxGuests = roomDetails?.totalMaxOccupancy;
    const threeSixtyImages = Array.isArray(roomDetails?.threeSixtyImages)
      ? roomDetails.threeSixtyImages.filter((img: any) => img?.script)
      : [];
    return {
      maxGuestsLabel:
        maxGuests > 1
          ? this._commonSvc.fillMessage(localeObj?.tf_2_RoomList_roomDetails_guestsMax, [maxGuests])
          : this._commonSvc.fillMessage(localeObj?.tf_2_RoomList_roomDetails_guestMax, [maxGuests]),
      roomSize: roomDetails?.roomSize,
      roomSizeUnits: roomDetails?.roomSizeUnits,
      threeSixtyImages,
    };
  });

  // View Child
  public readonly cardRef = viewChild(CardComponent, { read: ElementRef });

  constructor(
    public readonly _modalSvc: ModalService,
    public readonly _sidePanelSvc: SidePanelService,
    public readonly _storeSvc: StoreService,
    public readonly _commonSvc: CommonService,
    public readonly _roomRateAvailabilitySvc: RoomRateAvailabilityService,
    public readonly _shoppingCartSvc: ShoppingCartService,
  ) {
    // Room Details Effect
    effect(() => {
      const roomCode = untracked(() => this.data()?.code);
      const roomDetailsData = this._roomRateAvailabilitySvc.roomDetailsData();

      if (
        roomCode !== roomDetailsData?.roomCode ||
        (roomCode === roomDetailsData?.roomCode && roomDetailsData?.isLoading)
      ) {
        return;
      } else if (
        roomCode === roomDetailsData?.roomCode &&
        !roomDetailsData?.isLoading &&
        !roomDetailsData?.success &&
        roomDetailsData?.error
      ) {
        this._modalSvc.displayToastMessage({
          desc: roomDetailsData?.error?.message,
          type: 'error',
        });
        return;
      } else if (roomDetailsData) {
        this.openRoomDetails();
      }
    });
  }

  public onCardClick(event: Event) {
    const clickedElement = event.target as HTMLElement;
    if (!this.isExpanded() && this.isDisabled() && this.cardRef()?.nativeElement.contains(clickedElement)) {
      this.hideItems();
    }
  }

  public onBedTypeSelected(item: any) {
    if (item) {
      this.selectedBedTypeCode.set(item.id);
    }
  }

  public getRoomDetails(expanded?: boolean) {
    if (expanded) {
      this.roomDetailsClickedWhileCollapsed.set(true);
    }
    const roomCode = this.data()?.code;
    roomCode && this._roomRateAvailabilitySvc.getRoomDetailsForRoomCode(roomCode);
    this._roomRateAvailabilitySvc.generateAnalyticsItemsForList(
      [this.data()],
      this._storeSvc.userStore.currency()?.code,
      'rooms',
      'view_item',
    );
  }

  public openRoomDetails() {
    const isMobileScreen = untracked(() => this.isMobileScreen());
    if (isMobileScreen) {
      this._sidePanelSvc.openSidePanel(
        this.roomDetailsContentTemplate(),
        { onClose: () => this.closeRoomDetails() },
        'room-details-side-panel',
      );
    } else {
      this._modalSvc.openModal(this.roomDetailsContentTemplate(), {
        title: this.data()?.name,
        size: 'large',
        onClose: () => this.closeRoomDetails(),
      });
    }
  }

  public closeRoomDetails() {
    if (this.isMobileScreen()) {
      this._sidePanelSvc.closeSidePanel();
      if (!this.roomDetailsClickedWhileCollapsed()) {
        setTimeout(() => {
          document.body.style.overflow = 'hidden';
        }, 500);
      }
    }
    setTimeout(() => {
      this._roomRateAvailabilitySvc.clearRoomDetailsData();
      this.roomDetailsActiveTab.set('ROOM_DETAILS');
    }, 500);
  }

  public openRoomRatesListMobile() {
    this._sidePanelSvc.openSidePanel(this.roomRatesListContentTemplate(), {}, 'room-rates-list-mobile');
  }

  public closeRoomRatesListMobile() {
    this._sidePanelSvc.closeSidePanel('room-rates-list-mobile');
    this.onStateChanged.emit(null);
  }

  public viewItems() {
    if (!this.isExpanded()) {
      this.onStateChanged.emit({ index: this.index(), state: 'EXPANDED' });
    }

    if (this.isMobileScreen() && !this.isChild()) {
      this.openRoomRatesListMobile();
    }
    this._roomRateAvailabilitySvc.generateAnalyticsItemsForList(
      [this.data()],
      this._storeSvc.userStore.currency()?.code,
      'rooms',
      'select_content',
    );
  }

  public hideItems() {
    this.onStateChanged.emit(null);
  }

  public selectRoom() {
    this.onItemSelected.emit({
      room: this.data(),
      bedtype: {
        isDefault: this.data()?.defaultBedType === this.selectedBedTypeCode(),
        code: this.selectedBedTypeCode(),
      },
    });
    this._sidePanelSvc.closeSidePanel('rateplan-rooms-list-side-panel');
  }

  public selectRoomRate(item: any) {
    const defaultBedTypeCode = this.data()?.defaultBedType;
    const selectedBedTypeCode = this.selectedBedTypeCode();
    this.onRoomRateSelected.emit({
      room: this.data(),
      rate: item,
      bedtype: {
        isDefault: defaultBedTypeCode === selectedBedTypeCode,
        code: selectedBedTypeCode,
      },
    });

    if (this._sidePanelSvc.isSidePanelOpen('room-rates-list-mobile')) {
      this.closeRoomRatesListMobile();
    }
  }

  public getUpsellPrice(room: any, currencyCode: string, showPriceInclusiveOfFees: boolean) {
    const obj = room?.upsellPriceInfo;
    const upsellPrice = obj?.isUpsellRoom
      ? obj?.averageUpsellPriceByCurrency[currencyCode] || obj?.totalUpsellPriceByCurrency[currencyCode]
      : null;
    if (
      obj?.isUpsellRoom &&
      showPriceInclusiveOfFees &&
      room?.averagePriceByCurrency[currencyCode]?.totalTaxDisplay > 0
    ) {
      return upsellPrice + room?.averagePriceByCurrency[currencyCode]?.totalTaxDisplay;
    }
    return upsellPrice;
  }
}
